/**
 * Single-row CEFR ladder — six pill-bars across, each labeled below
 * with "<level> · <pct>%" or just "<level>" if untouched.
 *
 *   • Completed levels   → solid terracotta bar
 *   • Current level      → dark-terracotta partial fill on a paper-edge track
 *   • Future levels      → paper-edge track only
 *
 * The "spine" treatment from the design critique. Replaces the
 * earlier bar-chart visual; the detailed `CefrLevelGrid` stays
 * available for the deep-dive.
 */
import type { LevelBreakdown } from "../../api";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

export default function CEFRLadder({
  breakdowns,
  currentLevel,
}: {
  breakdowns: LevelBreakdown[];
  currentLevel: string;
}) {
  if (!breakdowns || breakdowns.length === 0) return null;

  const byLevel = new Map(breakdowns.map((b) => [b.level, b]));
  const currentIdx = LEVELS.indexOf(currentLevel);

  return (
    <div className="grid grid-cols-6 gap-3">
      {LEVELS.map((lvl, i) => {
        const b = byLevel.get(lvl);
        const pct = b ? Math.max(0, Math.min(100, Math.round(b.vocab_pct))) : 0;
        const isCurrent = lvl === currentLevel;
        const isCompleted = i < currentIdx;
        const isFuture = i > currentIdx;

        return (
          <div key={lvl} className="text-center">
            <div className="h-2 w-full rounded-full overflow-hidden bg-[rgba(42,36,26,0.10)]">
              {isCompleted && (
                <div className="h-full w-full bg-terracotta rounded-full" />
              )}
              {isCurrent && (
                <div
                  className="h-full bg-terracotta rounded-full transition-all"
                  style={{
                    width: `${pct}%`,
                    filter: pct < 100 ? "brightness(0.85)" : undefined,
                  }}
                />
              )}
            </div>
            <div
              className={
                "mt-2 font-mono text-[11px] tracking-wide " +
                (isCurrent
                  ? "text-ink font-medium"
                  : isFuture
                    ? "text-ink-faint/60"
                    : "text-ink-soft")
              }
            >
              {lvl}
              {(isCompleted || isCurrent) && (
                <>
                  {" · "}
                  <span className={isCurrent ? "text-terracotta font-medium" : ""}>
                    {pct}%
                  </span>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
